import React, { useState, useEffect } from "react";
import {
  IconDots,
  IconTrash,
  IconKeyboard,
  IconPlus,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Menu,
  Modal,
  Table,
  TextInput,
  Title,
} from "@mantine/core";

import { getUuid } from "@util/getUuid";

export default function EntityConfigLinks({ links = [], onUpdate }) {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);

  function onLinkUpdate(newLink) {
    let link = [...links].find((f) => f.id === newLink.id);
    if (!link) return;

    link.text = newLink.text;
    link.url = newLink.url;
    onUpdate(links);
  }

  return (
    <div>
      <Group>
        <Title order={4}>
          {t("mixer:microsite_settings:info_modal_links")}
        </Title>
        <Button
          variant="light"
          color="green"
          size="xs"
          leftSection={<IconPlus />}
          onClick={() => setOpen(true)}
        >
          {t("common:add")}
        </Button>
      </Group>
      <LinkModal
        isOpen={isOpen}
        onClose={() => setOpen(false)}
        onSubmit={(e) => {
          onUpdate([...links, e]);
          setOpen(false);
        }}
      />
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("common:text")}</Table.Th>
            <Table.Th>URL</Table.Th>
            <Table.Th></Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {links.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={3}>{t("common:no_results")}</Table.Td>
            </Table.Tr>
          )}
          {links.map((link) => (
            <LinkItem
              key={link.id}
              url={link.url}
              id={link.id}
              text={link.text}
              onRemoveClick={() =>
                onUpdate([...links].filter((f) => f.id !== link.id))
              }
              onUpdate={onLinkUpdate}
            />
          ))}
        </Table.Tbody>
      </Table>
    </div>
  );
}

const LinkModal = ({
  text = "",
  url = "",
  isOpen,
  onClose,
  id = null,
  onSubmit,
}) => {
  const { t } = useTranslation();
  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      title={
        id
          ? t("mixer:microsite_settings:update_link")
          : t("mixer:microsite_settings:create_link")
      }
    >
      <LinkForm id={id} text={text} url={url} onSubmit={onSubmit} />
    </Modal>
  );
};

const LinkItem = ({ id, url, text, onRemoveClick, onUpdate }) => {
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);

  return (
    <React.Fragment>
      <LinkModal
        isOpen={isOpen}
        onClose={(e) => setOpen(false)}
        id={id}
        text={text}
        url={url}
        onSubmit={(e) => {
          onUpdate(e);
          setOpen(false);
        }}
      />
      <Table.Tr>
        <Table.Td>{text}</Table.Td>
        <Table.Td>
          <Anchor href={url} target="_blank">
            {url}
          </Anchor>
        </Table.Td>
        <Table.Td>
          <Menu>
            <Menu.Target>
              <ActionIcon variant="subtle" size="sm" color="gray">
                <IconDots />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                component="button"
                onClick={() => setOpen(true)}
                icon={<IconKeyboard />}
              >
                {t("common:rename")}
              </Menu.Item>
              <Menu.Divider />
              <Menu.Item
                component="button"
                onClick={onRemoveClick}
                icon={<IconTrash />}
              >
                {t("common:remove")}
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Table.Td>
      </Table.Tr>
    </React.Fragment>
  );
};

const LinkForm = ({ text = "", url = "", id, onSubmit }) => {
  const { t } = useTranslation();
  const [formValues, setFormValues] = useState({
    text,
    url,
  });

  useEffect(() => {
    setFormValues({
      text,
      url,
    });
  }, []);

  return (
    <React.Fragment>
      <TextInput
        value={formValues.text}
        onChange={(e) => setFormValues({ ...formValues, text: e.target.value })}
        label={t("common:text")}
      />
      <TextInput
        value={formValues.url}
        onChange={(e) => setFormValues({ ...formValues, url: e.target.value })}
        label="URL"
      />
      {formValues.url && (
        <Anchor href={formValues.url} target="_blank" mt="lg">
          {t("mixer:microsite_settings:test_link")}
        </Anchor>
      )}
      <Button
        fullWidth
        color="green"
        variant="light"
        mt="lg"
        disabled={!formValues.text || !formValues.url}
        onClick={() => {
          onSubmit({
            ...formValues,
            id: id || getUuid(),
          });
        }}
      >
        {id ? t("common:update") : t("common:create")}
      </Button>
    </React.Fragment>
  );
};
